import React, { useState } from "react";
import { Box, Icon, Text, useIsMobile, type IconName } from "@salestrak/ui";

import { RowAction, copyText, openHref } from "./row-actions";
import type { Omittable } from "@salestrak/ui";

export type DetailFieldRowProps = Omittable<{
  icon?: IconName;
  label: string;
  value?: string | null;
  /** Shown muted when `value` is empty. Default "None". */
  emptyText?: string;
  /** "email" adds copy + open-mail; "phone" adds copy + open-call. */
  link?: "email" | "phone";
  /** Copy-only action. Ignored when `link` is set (link already copies). */
  copyable?: boolean;
  /** Clamps the value to N lines. */
  truncate?: 1 | 2 | 3;
  /** Default "sm" ("2xs" under `compact`). */
  labelTextSize?: "2xs" | "xs" | "sm";
  /** Default "sm" ("xs" under `compact`). */
  valueTextSize?: "2xs" | "xs" | "sm";
  /** Compact, icon-less density for the hover preview's property sheet. */
  compact?: boolean;
  /** Default "center"; pass "start" when the value is a tall vertical stack. */
  align?: "center" | "start";
  /** Value pinned to the row's right edge. Always shown. */
  trailing?: React.ReactNode;
  /** Overrides the `link`/`copyable`-derived actions. Use with `children`. */
  actions?: React.ReactNode;
  /** Custom value content; when present, `value`/`link`/`copyable` are ignored. */
  children?: React.ReactNode;
}>;

const LABEL_WIDTH = 80;

export function DetailFieldRow({
  icon,
  label,
  value,
  emptyText = "None",
  link,
  copyable,
  truncate,
  labelTextSize,
  valueTextSize,
  compact = false,
  align = "center",
  trailing,
  actions: actionsProp,
  children,
}: DetailFieldRowProps) {
  const isMobile = useIsMobile();
  const [revealed, setRevealed] = useState(false);

  const resolvedLabelSize = labelTextSize ?? (compact ? "2xs" : "sm");
  const resolvedValueSize = valueTextSize ?? (compact ? "xs" : "sm");

  const hasValue = !!value && value.trim().length > 0;
  const actions =
    actionsProp ??
    (!children && hasValue ? buildActions(value!, label, link, copyable) : null);

  // Same `revealed` flag, different trigger per platform: pointer on desktop,
  // tap-to-toggle on touch (no hover events fire there).
  const pointerHandlers =
    actions && !isMobile
      ? {
          onPointerEnter: () => setRevealed(true),
          onPointerLeave: () => setRevealed(false),
        }
      : {};
  const click = actions && isMobile ? () => setRevealed((v) => !v) : undefined;

  return (
    <Box
      direction="row"
      align={align}
      gap="sm"
      minHeight={24}
      onClick={click}
      {...pointerHandlers}
    >
      {icon ? <Icon name={icon} size="sm" color="muted" /> : null}
      <Box width={LABEL_WIDTH} shrink={false}>
        <Text
          size={resolvedLabelSize}
          weight={resolvedLabelSize === "sm" ? undefined : "medium"}
          variant="muted"
        >
          {label}
        </Text>
      </Box>
      <Box flex={1} shrink>
        {children ?? (
          <Text
            size={resolvedValueSize}
            variant={hasValue ? undefined : "muted"}
            truncate={truncate}
          >
            {hasValue ? value : emptyText}
          </Text>
        )}
      </Box>
      {actions && revealed ? (
        <Box direction="row" align="center" gap="2xs" shrink={false}>
          {actions}
        </Box>
      ) : null}
      {trailing ? <Box shrink={false}>{trailing}</Box> : null}
    </Box>
  );
}

function buildActions(
  value: string,
  label: string,
  link: DetailFieldRowProps["link"],
  copyable: boolean | undefined,
): React.ReactNode {
  if (link === "email") {
    return (
      <>
        <RowAction icon="mail" onClick={() => openHref(`mailto:${value}`)} />
        <RowAction icon="copy" onClick={() => copyText(value, "Email")} />
      </>
    );
  }
  if (link === "phone") {
    return (
      <>
        <RowAction icon="phone" onClick={() => openHref(`tel:${value}`)} />
        <RowAction icon="copy" onClick={() => copyText(value, "Phone")} />
      </>
    );
  }
  if (copyable) {
    return <RowAction icon="copy" onClick={() => copyText(value, label)} />;
  }
  return null;
}
